import React from 'react';
import { Box, ButtonBase, Skeleton, Tooltip, Typography } from '@mui/material';
import { format, parseISO } from 'date-fns';
import { tokens } from '../../theme/tokens';
import {
  TILE_COPY,
  TILE_ORDER,
  tileSubline,
  type StoreSyncRunResult,
  type StoreSyncSummary,
  type StoreSyncTile,
} from './model';

const RESULT_COLOR: Record<StoreSyncRunResult, string> = {
  clean: tokens.signal.positive.solid,
  warnings: tokens.signal.caution.solid,
  failed: tokens.signal.problem.solid,
};

export const RESULT_LABEL: Record<StoreSyncRunResult, string> = {
  clean: 'Clean',
  warnings: 'Warnings',
  failed: 'Failed',
};

export { RESULT_COLOR };

interface HealthOverviewProps {
  summary: StoreSyncSummary | undefined;
  loading: boolean;
  activeTile: StoreSyncTile | null;
  onTile: (tile: StoreSyncTile | null) => void;
}

/** Clickable health tiles plus the 14-day fleet chart. A tile filters the table; clicking it again clears it. */
export const HealthOverview: React.FC<HealthOverviewProps> = ({ summary, loading, activeTile, onTile }) => {
  const days = summary?.fleet.days ?? [];
  const maxDay = Math.max(1, ...days.map((d) => d.clean + d.warnings + d.failed));

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '14px', alignItems: 'stretch' }}>
      <Box
        sx={{
          flex: '2 1 520px',
          minWidth: 0,
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(auto-fit, minmax(130px, 1fr))' },
          gap: '10px',
        }}
      >
        {TILE_ORDER.map((tile) => {
          const copy = TILE_COPY[tile];
          const palette = tokens.signal[copy.tone];
          const active = activeTile === tile;
          const count = summary?.tiles[tile] ?? 0;
          return (
            <ButtonBase
              key={tile}
              onClick={() => onTile(active ? null : tile)}
              aria-pressed={active}
              disabled={loading && !summary}
              sx={{
                display: 'block',
                textAlign: 'left',
                p: '12px 14px',
                borderRadius: `${tokens.radius.card}px`,
                border: `1px solid ${active ? palette.solid : tokens.line.default}`,
                bgcolor: active ? palette.tint : tokens.surface.card,
                boxShadow: active ? `0 0 0 3px ${palette.tint}` : 'none',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: palette.solid, flexShrink: 0 }} />
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: palette.ink }}>{copy.label}</Typography>
              </Box>
              {loading && !summary ? (
                <Skeleton width={48} height={36} />
              ) : (
                <Typography sx={{ fontSize: 24, fontWeight: 700, fontVariantNumeric: 'tabular-nums', lineHeight: 1.3 }}>
                  {count}
                </Typography>
              )}
              <Typography sx={{ fontSize: 11.5, color: tokens.ink.muted }}>
                {summary ? tileSubline(tile, summary) : ' '}
              </Typography>
            </ButtonBase>
          );
        })}
      </Box>

      <Box
        sx={{
          flex: '1 1 300px',
          minWidth: 0,
          bgcolor: tokens.surface.card,
          border: `1px solid ${tokens.line.default}`,
          borderRadius: `${tokens.radius.card}px`,
          p: '12px 14px',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: tokens.ink.bodyAlt }}>Runs, last 14 days</Typography>
          <Typography sx={{ fontSize: 12, color: tokens.ink.muted }}>
            {summary
              ? `${summary.fleet.runs} runs${summary.fleet.withoutErrorsPct === null ? '' : ` · ${summary.fleet.withoutErrorsPct}% without errors`}`
              : ' '}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '4px', height: 54, mt: 1.25 }}>
          {days.map((d) => {
            const total = d.clean + d.warnings + d.failed;
            const label = `${format(parseISO(d.date), 'MMM d')}: ${d.clean} clean, ${d.warnings} with warnings, ${d.failed} failed`;
            return (
              <Tooltip key={d.date} title={label} arrow>
                <Box
                  role="img"
                  aria-label={label}
                  sx={{
                    flex: 1,
                    height: `${Math.max(total === 0 ? 0 : 6, (total / maxDay) * 100)}%`,
                    minHeight: total === 0 ? 2 : 0,
                    bgcolor: total === 0 ? tokens.line.default : undefined,
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: '2px',
                    overflow: 'hidden',
                  }}
                >
                  {total > 0 && (
                    <>
                      <Box sx={{ flex: d.failed, bgcolor: RESULT_COLOR.failed }} />
                      <Box sx={{ flex: d.warnings, bgcolor: RESULT_COLOR.warnings }} />
                      <Box sx={{ flex: d.clean, bgcolor: RESULT_COLOR.clean }} />
                    </>
                  )}
                </Box>
              </Tooltip>
            );
          })}
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: 1, alignItems: 'center' }}>
          {(['clean', 'warnings', 'failed'] as StoreSyncRunResult[]).map((r) => (
            <Box key={r} sx={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: 11, color: tokens.ink.muted }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: RESULT_COLOR[r] }} />
              {RESULT_LABEL[r]}
            </Box>
          ))}
          {days.length > 0 && (
            <Typography sx={{ fontSize: 11, color: tokens.ink.faint, ml: 'auto' }}>
              {format(parseISO(days[0].date), 'MMM d')} – {format(parseISO(days[days.length - 1].date), 'MMM d')}
            </Typography>
          )}
        </Box>
        {summary?.truncated?.chart ? (
          <Typography sx={{ fontSize: 11, color: tokens.signal.caution.ink, mt: 0.75 }}>
            Some days are counted from a capped read and may be low until the hourly rollup counts them.
          </Typography>
        ) : summary?.truncated?.runs ? (
          <Typography sx={{ fontSize: 11, color: tokens.ink.faint, mt: 0.75 }}>
            Run history is capped at the newest 20,000 runs, so older run strips may be short.
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
};
